// VendorFormContext.js
import React, { createContext, useContext, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createVendor, getVendorDetailsById } from "../../../api/sales";
import { toast } from 'react-toastify';

const VendorFormContext = createContext();

// Define tab order for validation tracking
export const SUMMARY_TABS = ['account-info', 'contact', 'billing'];
export const DETAILS_TABS = ['company-profile', 'company-preference', 'billing-terms', 'description'];
export const ALL_TABS = [...SUMMARY_TABS, ...DETAILS_TABS];

// Pure helpers — used by saveVendorData and saveAllChanges
const toInt = (val) => {
  if (val === null || val === undefined || val === '') return undefined;
  const n = parseInt(val, 10);
  return isNaN(n) ? undefined : n;
};
const toNum = (val) => {
  if (val === null || val === undefined || val === '') return undefined;
  const n = Number(val);
  return isNaN(n) ? undefined : n;
};

// Normalize values for comparison: treat undefined, null, and "" as equivalent (empty)
// Also normalize numeric strings to numbers (e.g., "501" and 501 are equal)
// This prevents false positives when forms initialize empty fields or convert types
const normalizeForCompare = (obj) => {
  if (obj === null || obj === undefined || obj === '') return undefined;
  if (Array.isArray(obj)) return obj.map(normalizeForCompare);
  if (typeof obj === 'object') {
    const normalized = {};
    for (const [key, value] of Object.entries(obj)) {
      const nv = normalizeForCompare(value);
      if (nv !== undefined) normalized[key] = nv;
    }
    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }
  // Normalize numeric strings to numbers so "501" === 501
  if (typeof obj === 'string' && obj.trim() !== '' && !isNaN(Number(obj))) {
    return Number(obj);
  }
  return obj;
};

export const VendorFormProvider = ({ children }) => {
  const navigate = useNavigate();
  const initialFormData = {
    accountInfo: {},
    contacts: [],
    billingInfo: {},
    companyProfile: {},
    companyPreference: {},
    billingTerms: {},
    description: ''
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isCreating, setIsCreating] = useState(true); // true = create, false = update
  const [isSaving, setIsSaving] = useState(false);
  const [serverFormData, setServerFormData] = useState(initialFormData);

  // Track completed/validated tabs - initially only first tab is accessible
  const [completedTabs, setCompletedTabs] = useState([]);
  const [currentTab, setCurrentTab] = useState('account-info');

  // Global unsaved changes detection — compares formData vs last-saved server snapshot
  // Uses normalizeForCompare to avoid false positives from empty-value mismatches
  const hasUnsavedChanges = useMemo(() => {
    if (isCreating) return false;
    const sections = ['accountInfo', 'contacts', 'billingInfo', 'companyProfile',
                      'companyPreference', 'billingTerms', 'description'];
    for (const section of sections) {
      if (JSON.stringify(normalizeForCompare(formData[section])) !== JSON.stringify(normalizeForCompare(serverFormData[section]))) {
        return true;
      }
    }
    return false;
  }, [formData, serverFormData, isCreating]);

  const updateFormData = (section, data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  // Call this before starting a new vendor creation
  const startCreateNewVendor = () => {
    setFormData(initialFormData);
    setServerFormData(initialFormData);
    setIsCreating(true);
    setCompletedTabs([]); // Reset completed tabs
    setCurrentTab('account-info'); // Reset to first tab
  };

  // Mark a tab as completed (validated)
  const markTabComplete = (tabLink) => {
    setCompletedTabs((prev) => {
      if (!prev.includes(tabLink)) {
        return [...prev, tabLink];
      }
      return prev;
    });
  };

  // Mark a tab as incomplete (has errors)
  const markTabIncomplete = (tabLink) => {
    setCompletedTabs((prev) => prev.filter((t) => t !== tabLink));
  };

  // Check if a tab can be accessed
  const canAccessTab = (tabLink) => {
    // In update mode, all tabs are accessible
    if (!isCreating) return true;

    // First tab is always accessible
    const tabIndex = ALL_TABS.indexOf(tabLink);
    if (tabIndex === 0) return true;

    // Check if all previous tabs are completed
    const previousTabs = ALL_TABS.slice(0, tabIndex);
    return previousTabs.every((tab) => completedTabs.includes(tab));
  };

  // Get the next tab link
  const getNextTab = (currentTabLink) => {
    const currentIndex = ALL_TABS.indexOf(currentTabLink);
    if (currentIndex < ALL_TABS.length - 1) {
      return ALL_TABS[currentIndex + 1];
    }
    return null;
  };

  // Get the previous tab link
  const getPrevTab = (currentTabLink) => {
    const currentIndex = ALL_TABS.indexOf(currentTabLink);
    if (currentIndex > 0) {
      return ALL_TABS[currentIndex - 1];
    }
    return null;
  };

  // Check if Summary section is complete (all summary tabs validated)
  const isSummaryComplete = () => {
    if (!isCreating) return true; // In update mode, always accessible
    return SUMMARY_TABS.every((tab) => completedTabs.includes(tab));
  };

  // Check if Details section is complete (all details tabs validated)
  const isDetailsComplete = () => {
    if (!isCreating) return true; // In update mode, always accessible
    return DETAILS_TABS.every((tab) => completedTabs.includes(tab));
  };

  // Check if a main section can be accessed (summary, details, files)
  const canAccessSection = (sectionLink) => {
    // In update mode, all sections are accessible
    if (!isCreating) return true;

    switch (sectionLink) {
      case 'summary':
        return true; // Summary is always accessible
      case 'details':
        return isSummaryComplete(); // Details requires Summary to be complete
      case 'files':
        return isSummaryComplete() && isDetailsComplete(); // Files requires both Summary and Details
      default:
        return false;
    }
  };

  // Helper: remove null, undefined, and empty-string keys from an object (shallow)
  const stripEmpty = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const cleaned = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  // Format contacts array for API: [{ contact_id: "67", contact_type: "Primary" }, ...]
  const formatContactsForApi = (contactsArray) => {
    if (!Array.isArray(contactsArray) || contactsArray.length === 0) return undefined;
    return contactsArray.map((contact) => ({
      contact_id: String(contact.contact_id || contact.contactId || ''),
      contact_type: contact.isPrimary ? 'Primary' : 'Secondary',
    }));
  };

  // options: { skipToast, skipSavingState } — used by saveAllChanges to batch saves
  const saveVendorData = async (section, sectionData, options = {}) => {
    const { skipToast = false, skipSavingState = false } = options;

    // Ensure creditLimit and creditHold are numbers if present in billingTerms
    let billingTerms = { ...formData.billingTerms };
    if (section === "billingTerms" && sectionData) {
      billingTerms = { ...billingTerms, ...sectionData };
    }
    if (billingTerms.creditLimit !== undefined && billingTerms.creditLimit !== null) {
      billingTerms.creditLimit = Number(billingTerms.creditLimit);
    }
    if (billingTerms.creditHold !== undefined && billingTerms.creditHold !== null) {
      billingTerms.creditHold = Number(billingTerms.creditHold);
    }


    // Get selectedVendor from Redux directly (import store)
    let selectedVendorId = null;
    try {
      const { store } = await import("../../../store/store.js");
      const vendorState = store.getState().vendor;
      if (vendorState && vendorState.selectedVendor && vendorState.selectedVendor.vendor_id) {
        selectedVendorId = vendorState.selectedVendor.vendor_id;
      }
    } catch (e) {
      // fallback: do nothing
    }

    // If isCreating is true or no selectedVendorId, treat as create
    let payload;
    if (!isCreating && selectedVendorId) {
      // PUT: map section data to valid API field names and types
      switch (section) {
        case 'accountInfo': {
          const d = sectionData || {};
          payload = {};
          if (d.name != null) payload.name = d.name?.trim() || undefined;
          if (d.type != null) payload.type = d.type || undefined;
          if (d.account_type != null || d.accountType != null)
            payload.accountType = d.account_type || d.accountType || undefined;
          if (d.annual_revenue != null || d.annualRevenue != null)
            payload.annualRevenue = toNum(d.annual_revenue ?? d.annualRevenue);
          if (d.website != null) payload.website = d.website?.trim() || undefined;
          if (d.status != null) payload.status = d.status || undefined;
          if (d.phone != null) payload.phone = d.phone || undefined;
          if (d.phoneCountry != null) payload.phoneCountry = toNum(d.phoneCountry);
          if (d.fax != null) payload.fax = d.fax || undefined;
          if (d.faxCountry != null) payload.faxCountry = toNum(d.faxCountry);
          // Remove undefined keys
          Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });
          break;
        }
        case 'contacts': {
          const contactsArr = sectionData?.contacts;
          payload = { contacts: Array.isArray(contactsArr) ? formatContactsForApi(contactsArr) : undefined };
          break;
        }
        case 'billingInfo': {
          payload = { billToAddress: stripEmpty(sectionData || {}) };
          break;
        }
        case 'companyProfile': {
          const d = sectionData || {};
          payload = {};
          if (d.industry != null) payload.industry = d.industry?.trim() || undefined;
          if (d.yearStarted != null) payload.yearStarted = toInt(d.yearStarted);
          if (d.totalEmployees != null) payload.totalEmployees = toInt(d.totalEmployees);
          if (d.ownership != null) payload.ownership = d.ownership || undefined;
          // DUNS and NAICS excluded from update per original logic
          Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });
          break;
        }
        case 'companyPreference': {
          const d = sectionData || {};
          payload = {};
          if (d.contactMethod != null) payload.contactMethod = d.contactMethod || undefined;
          if (d.email != null) payload.email = d.email?.trim() || undefined;
          if (d.followupEmail != null) payload.followupEmail = d.followupEmail?.trim() || undefined;
          if (d.bulkEmail != null) payload.bulkEmail = d.bulkEmail === true ? true : false;
          Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });
          break;
        }
        case 'billingTerms': {
          const d = sectionData || {};
          payload = {};
          if (d.currency != null) payload.currency = d.currency?.trim() || undefined;
          if (d.creditLimit != null) payload.creditLimit = toNum(d.creditLimit);
          if (d.creditHold != null) payload.creditHold = toNum(d.creditHold);
          Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });
          break;
        }
        default:
          // For any other section, pass through but strip empty values
          payload = stripEmpty(sectionData || {});
      }
    } else {
      // POST: build payload with ONLY valid API fields (no spreads to prevent leaks)
      const acct = formData.accountInfo || {};
      const prof = formData.companyProfile || {};
      const pref = formData.companyPreference || {};
      const bill = formData.billingInfo || {};

      payload = {
        // accountInfo — map snake_case form names to camelCase API names
        name: acct.name?.trim() || undefined,
        type: acct.type || undefined,
        accountType: acct.account_type || acct.accountType || undefined,
        annualRevenue: toNum(acct.annual_revenue ?? acct.annualRevenue),
        website: acct.website?.trim() || undefined,
        status: acct.status || undefined,
        phone: acct.phone || undefined,
        phoneCountry: toNum(acct.phoneCountry),
        fax: acct.fax || undefined,
        faxCountry: toNum(acct.faxCountry),

        // contacts — format as [{ contact_id, contact_type }]
        contacts: formatContactsForApi(formData.contacts),

        // billing address
        billToAddress: Object.keys(stripEmpty(bill)).length > 0 ? stripEmpty(bill) : undefined,

        // companyProfile — pick only valid API fields
        industry: prof.industry?.trim() || undefined,
        yearStarted: toInt(prof.yearStarted),
        totalEmployees: toInt(prof.totalEmployees),
        DUNS: toInt(prof.DUNS),
        NAICS: toInt(prof.NAICS),
        ownership: prof.ownership || undefined,

        // companyPreference — pick only valid API fields
        contactMethod: pref.contactMethod || undefined,
        email: pref.email?.trim() || undefined,
        followupEmail: pref.followupEmail?.trim() || undefined,
        bulkEmail: pref.bulkEmail === true ? true : undefined,

        // billingTerms — pick only valid API fields
        currency: billingTerms.currency?.trim() || undefined,
        creditLimit: toNum(billingTerms.creditLimit),
        creditHold: toNum(billingTerms.creditHold),

        // NOTE: 'description' is NOT a valid API field — intentionally omitted
      };

      // Final cleanup: remove any undefined keys
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) delete payload[key];
      });
    }

    updateFormData(section, sectionData); // update context state immediately

    if (!skipSavingState) setIsSaving(true);
    try {
      let res;
      if (!isCreating && selectedVendorId) {
        // PUT update
        res = await import("../../../api/sales").then(m => m.updateVendor(payload, selectedVendorId));
        if (!skipToast) toast.success('Vendor updated successfully');
      } else {
        // POST create
        res = await import("../../../api/sales").then(m => m.createVendor(payload));
        if (!skipToast) toast.success('Vendor created successfully');
        setIsCreating(false); // After creation, switch to update mode
        // Navigate back to vendor listing after successful creation
        navigate('/sales/vendors');
      }
      // Update server snapshot for this section
      setServerFormData(prev => ({...prev, [section]: sectionData}));
    } catch (error) {
      if (!skipToast) {
        if (error.response) {
          const { status } = error.response;

          if (status === 400) {
            toast.error('Invalid data provided. Please check your inputs.');
          } else if (status === 500) {
            toast.error('Server error. Please try again later.');
          } else {
            toast.error('An error occurred. Please try again.');
          }
        } else if (!error.response) {
          toast.error('Unable to connect to server. Please check your connection.');
        } else {
          toast.error('An unexpected error occurred.');
        }
      }

      // Rethrow so saveAllChanges knows about the failure
      if (skipToast) throw error;
    } finally {
      if (!skipSavingState) setIsSaving(false);
    }
  };

  // Save all changed sections at once — used by global Save button
  const saveAllChanges = async () => {
    if (isCreating) return;

    const simpleSections = ['accountInfo', 'companyProfile', 'companyPreference', 'billingTerms', 'description'];
    const changedSections = [];

    for (const section of simpleSections) {
      if (JSON.stringify(normalizeForCompare(formData[section])) !== JSON.stringify(normalizeForCompare(serverFormData[section]))) {
        changedSections.push({ section, data: formData[section] });
      }
    }
    // contacts and billingInfo need special wrapping for the API
    if (JSON.stringify(normalizeForCompare(formData.contacts)) !== JSON.stringify(normalizeForCompare(serverFormData.contacts))) {
      changedSections.push({ section: 'contacts', data: { contacts: formData.contacts } });
    }
    if (JSON.stringify(normalizeForCompare(formData.billingInfo)) !== JSON.stringify(normalizeForCompare(serverFormData.billingInfo))) {
      changedSections.push({ section: 'billingInfo', data: formData.billingInfo });
    }

    if (changedSections.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setIsSaving(true);
    try {
      for (const { section, data } of changedSections) {
        await saveVendorData(section, data, { skipToast: true, skipSavingState: true });
      }
      setServerFormData(JSON.parse(JSON.stringify(formData)));
      toast.success('All changes saved successfully');
    } catch (error) {
      // Error toast already shown by saveVendorData
    } finally {
      setIsSaving(false);
    }
  };

  const resetFormData = () => {
    setFormData(initialFormData);
    setServerFormData(initialFormData);
    setIsCreating(true);
  };

  const resetVendorData = (newData) => {
    setFormData(newData);
    setServerFormData(JSON.parse(JSON.stringify(newData))); // Deep clone as server baseline
    setIsCreating(false);
    // In update mode, mark all tabs as accessible/completed
    setCompletedTabs([...ALL_TABS]);
  };

  return (
    <VendorFormContext.Provider value={{
      formData,
      updateFormData,
      saveVendorData,
      saveAllChanges,
      resetVendorData,
      resetFormData,
      isCreating,
      isSaving,
      hasUnsavedChanges,
      startCreateNewVendor,
      // Tab validation functions
      completedTabs,
      currentTab,
      setCurrentTab,
      markTabComplete,
      markTabIncomplete,
      canAccessTab,
      getNextTab,
      getPrevTab,
      // Section validation functions
      isSummaryComplete,
      isDetailsComplete,
      canAccessSection
    }}>
      {children}
    </VendorFormContext.Provider>
  );
};

export const useVendorForm = () => useContext(VendorFormContext);
